import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import EditableField from "../../EditableField/EditableField";
import Components from "../LandingComponents";
import { updateEventAttribute } from "../../../data-store/actions/events-actions";

const IDIOMAS_LABELS = {
  en: "Inglés",
  fr: "Francés",
  it: "Italiano",
  de: "Alemán",
  pt: "Portugués",
};

const useStyles = makeStyles((theme) => ({
  seccionTitulo: {
    margin: "20px 0 10px 0",
  },
  celdaBase: {
    maxWidth: 260,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: theme.palette.text.secondary,
  },
  celdaClave: {
    fontFamily: "monospace",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
}));

// Tipos de campo que representan contenido visible para el atleta
// (texto o imagen). Los demás (select/color/switch) son configuración,
// no contenido traducible.
const TIPOS_TRADUCIBLES = ["text", "photo", "file"];

function obtenerCamposTraducibles(componentType) {
  const descripcion =
    Components[componentType] && Components[componentType].componentDescription;
  if (!descripcion || !descripcion.componentConfig) return [];

  return Object.keys(descripcion.componentConfig)
    .map((fieldName) => ({ fieldName, ...descripcion.componentConfig[fieldName] }))
    .filter(
      (campo) =>
        TIPOS_TRADUCIBLES.includes(campo.type) &&
        campo.traducible !== false &&
        // Los campos repetibles (galería de fotos, redes sociales, menú del
        // header, etc.) declaran su propio "type" (ej. "text") a nivel del
        // campo contenedor aunque en realidad son un arreglo de objetos
        // descrito por inputsArray — si se incluyeran acá, se intentaría
        // renderizar ese arreglo completo como si fuera un texto simple y
        // React truena ("Objects are not valid as a React child"). Estos se
        // manejan aparte en obtenerCamposArrayTraducibles, uno por ítem.
        campo.inputsArray === undefined
    );
}

// Campos repetibles (inputsArray) que tienen al menos un sub-campo de
// contenido visible (ej. el "text" de cada item del menú del header, pero
// no su "url"). Se traduce un campo por cada ítem ya guardado, no el
// arreglo completo de una sola vez.
function obtenerCamposArrayTraducibles(componentType) {
  const descripcion =
    Components[componentType] && Components[componentType].componentDescription;
  if (!descripcion || !descripcion.componentConfig) return [];

  return Object.keys(descripcion.componentConfig)
    .map((fieldName) => ({ fieldName, ...descripcion.componentConfig[fieldName] }))
    .filter((campo) => campo.inputsArray !== undefined)
    .map((campo) => ({
      ...campo,
      subCamposTraducibles: Object.keys(campo.inputsArray)
        .map((subFieldName) => ({
          fieldName: subFieldName,
          ...campo.inputsArray[subFieldName],
        }))
        .filter(
          (subCampo) =>
            TIPOS_TRADUCIBLES.includes(subCampo.type) &&
            subCampo.traducible !== false
        ),
    }))
    .filter((campo) => campo.subCamposTraducibles.length > 0);
}

// Diccionario base (español) de los textos fijos de react-i18next del flujo
// de inscripción (namespace "inscripcion" en atletas/dashport-atletas). Es un
// espejo manual: si se agregan claves nuevas en
// atletas/dashport-atletas/src/i18n/locales/es/inscripcion.json, agrégalas
// también aquí para que el organizador pueda sobreescribirlas por evento.
const TEXTOS_FIJOS_BASE = {
  general: {
    confirmaDatosRegistro: "Confirma los datos de registro",
    confirmarMisDatos: "Confirmar mis datos",
    regresarADatos: "Regresar a los Datos",
    continuar: "Continuar",
    excelente: "Excelente {{nombre}}",
    puedesContinuarRegistro: "Puedes continuar con el registro",
    regresar: "Regresar",
    felicidades: "Felicidades {{nombre}}",
    detalleCompra:
      "A continuación el detalle de tu compra, de igual manera recibirás un correo con la información",
    grupoRecuperado_one:
      "Recuperamos {{count}} registro pendiente de antes (sin pagar todavía).",
    grupoRecuperado_other:
      "Recuperamos {{count}} registros pendientes de antes (sin pagar todavía).",
    continuarConRegistros: "Continuar con esos registros",
    descartarEmpezarDeNuevo: "Descartar y empezar de nuevo",
    descripcionOrdenInscripcion: "Inscripción al evento {{evento}} - {{modalidad}}",
    comisionDescripcion: "Comisión por registro y pago en linea",
    comisionNombre: "Comisión OnLine",
    cargando: "Cargando...",
    inscripciones: "Inscripciones",
    guardarYContinuar: "Guardar y continuar",
  },
  camposFormulario: {
    "camposFormulario.correo.etiqueta": "Correo",
    "camposFormulario.correo.validacion": "Campo Requerido no puede incluir espacios",
    "camposFormulario.nombres.etiqueta": "Nombre(s)",
    "camposFormulario.apellidos.etiqueta": "Apellido(s)",
    "camposFormulario.apellidoMaterno.etiqueta": "Apellido Materno",
    "camposFormulario.apellidoPaterno.etiqueta": "Apellido Paterno",
    "camposFormulario.telefono.etiqueta": "Teléfono",
    "camposFormulario.telefono.validacion": "Mínimo 10 dígitos (solo números)",
    "camposFormulario.fechaNacimiento.etiqueta": "Fecha de Nacimiento",
    "camposFormulario.fechaNacimiento.observacion":
      "Campo de requerido, Revisa el formato (AAAA-MM-DD)",
    "camposFormulario.fechaNacimiento.errorMessage": "Revisa el formato (AAAA-MM-DD)",
    "camposFormulario.genero.etiqueta": "Género",
    "camposFormulario.genero.selectLabel": "Genero",
    "camposFormulario.genero.femenino": "Femenino",
    "camposFormulario.genero.masculino": "Masculino",
    "camposFormulario.campoRequeridoGenerico": "Campo de requerido",
  },
  modalidadesCategorias: {
    "modalidadesCategorias.seleccionaGeneroFecha":
      "Selecciona el género y fecha de nacimiento para continuar",
    "modalidadesCategorias.titulo": "MODALIDADES Y CATEGORÍAS",
    "modalidadesCategorias.subtitulo":
      "Selecciona la categoría dentro de la modalidad que deseas realizar. Las opciones mostradas están filtradas con base al género y fecha de nacimiento registrados.",
    "modalidadesCategorias.agotado": "AGOTADO",
    "modalidadesCategorias.sinCategoriasParaGeneroEdad":
      "Esta distancia no tiene categorías para el género seleccionado y edad seleccionada",
    "modalidadesCategorias.edadDesdeYMayores": "{{edadDesde}} años y mayores",
    "modalidadesCategorias.edadDesdeHasta": "{{edadDesde}} a {{edadHasta}} años",
    "modalidadesCategorias.precio": "Precio : {{monto}} {{moneda}}",
  },
  moneda: {
    "moneda.monedaOficial": "Moneda oficial del evento: {{texto}} ({{codigo}})",
    "moneda.seleccionaMoneda": "Selecciona la moneda que deseas usar",
    "moneda.monedaActual": "Moneda actual: ",
    "moneda.tipoDeCambio": "Tipo de cambio: {{tipoCambio}}",
  },
  producto: {
    "producto.agotadoSufijo": " (Agotado)",
    "producto.precio": "Precio: ",
    "producto.cantidad": "Cantidad: {{cantidad}}",
    "producto.total": "Total: ",
    "producto.guardarDatos": "Guardar datos",
    "producto.agotado": "Agotado",
    "producto.agregarAlRegistro": "Agregar al registro",
    "producto.eliminar": "Eliminar",
  },
  detalleOrden: {
    "detalleOrden.estadoDeCuenta": "Estado de Cuenta",
    "detalleOrden.producto": "Producto",
    "detalleOrden.descripcion": "Descripción",
    "detalleOrden.cantidad": "Cantidad",
    "detalleOrden.precio": "Precio",
    "detalleOrden.total": "Total",
    "detalleOrden.totalAPagar": "Total a pagar",
    "detalleOrden.pagoEnPesosMexicanos":
      "Pago en pesos Méxicanos a tasa {{tasa}} MXN/{{codigo}}- Total {{totalMxn}} MXN",
    "detalleOrden.totalEnMoneda": "Total en {{texto}}",
    "detalleOrden.pesosMexicanosCalculado":
      "Pesos Méxicanos calculado a tasa de {{tasa}} MXN/{{codigo}}",
  },
  grupo: {
    "grupo.inscripcionDe": "Inscripción de {{nombres}} {{apellidos}}",
    "grupo.distanciaRamaCategoria": "{{distancia}} · {{rama}} · {{categoria}}",
    "grupo.pagoIncluidoEnInscripcionDe":
      "Pago incluido en la inscripción de {{nombres}} {{apellidos}}",
    "grupo.folioDeRegistro": "Folio/ID de registro: {{inscritoKey}}",
    "grupo.resumenDelGrupo": "Resumen del grupo",
    "grupo.pagoUnaSolaVez":
      "El pago se hace una sola vez, a nombre de {{nombres}} {{apellidos}}. Los demás atletas quedan registrados sin costo adicional para ellos.",
    "grupo.pagaInscripcionDeTodos": "(paga la inscripción de todos)",
    "grupo.pagarTodo": "Pagar todo",
    "grupo.cancelarGrupo": "Cancelar grupo y empezar de nuevo",
  },
  pago: {
    "pago.totalAPagar": "Total a pagar $",
    "pago.tarjeta": "Tarjeta de Crédito o Débito",
    "pago.oxxo": "Pago en Oxxo",
    "pago.finalizarRegistro": "Finalizar Registro",
    "pago.cambiarMetodoDePago": "Cambiar método de pago",
    "pago.pagoExitoso": "Pago exitoso ",
    "pago.errorIntenteDeNuevo": "Error en el registro intente de nuevo",
  },
  guardarYAgregar: {
    "guardarYAgregar.continuarResumenGrupo": "Continuar y ver resumen del grupo",
    "guardarYAgregar.continuarConPago": "Continuar con el pago",
    "guardarYAgregar.continuarFinalizar": "Continuar para Finalizar proceso",
    "guardarYAgregar.guardando": "Guardando...",
    "guardarYAgregar.agregarOtroParticipante": "Agregar otro participante",
  },
  confirmarCorreo: {
    "confirmarCorreo.revisaYConfirma": "Revisa y confirma el correo antes de continuar",
    "confirmarCorreo.confirmarMiCorreo": "Confirmar mi correo",
  },
  oxxo: {
    "oxxo.felicidadesCompletaste": "Felicidades completaste con exito el registro de datos.",
    "oxxo.fichaOxxoAviso":
      "A continuación se muestra la ficha para pago en oxxo, de igual manera la recibiras en tu correo electrónico.",
    "oxxo.vigenciaPrefijo": "La ficha de pago tiene una vigencia de ",
    "oxxo.horas48": "48 horas",
    "oxxo.vigenciaSufijo":
      ", de no realizarse el pago en ese periodo el registro y número asignado quedará eliminado",
    "oxxo.fichaDigital": "FICHA DIGITAL. NO ES NECESARIO IMPRIMIR.",
    "oxxo.montoAPagar": "Monto a pagar",
    "oxxo.comisionAdicional":
      "OXXO cobrará una comisión adicional al momento de realizar el pago.",
    "oxxo.referencia": "Referencia",
    "oxxo.instrucciones": "Instrucciones",
    "oxxo.paso1Prefijo": "Acude a la tienda OXXO más cercana. ",
    "oxxo.paso1Link": "Encuéntrala aquí.",
    "oxxo.paso2Prefijo": "Indica en caja que quieres realizar un pago de ",
    "oxxo.paso2Sufijo": ".",
    "oxxo.paso3":
      "Dicta al cajero el número de referencia en esta ficha para que lo teclee directamente en la pantalla de venta.",
    "oxxo.paso4": "Realiza el pago correspondiente con dinero en efectivo.",
    "oxxo.paso5Prefijo": "Al confirmar tu pago, el cajero te entregará un comprobante impreso. ",
    "oxxo.paso5Destacado":
      "En él podrás verificar que se haya realizado correctamente. Conserva este comprobante de pago.",
    "oxxo.footerPrefijo": "Al completar estos pasos recibirás un correo de ",
    "oxxo.footerSufijo": " confirmando tu pago.",
  },
  oxxoFicha: {
    "oxxoFicha.fichaDigital": "Ficha digital. No es necesario imprimir.",
    "oxxoFicha.encuentralaAqui": "Encuéntrala aquí",
    "oxxoFicha.pagoDeOxxopay": "Indica en caja que quieres realizar un pago de",
    "oxxoFicha.instruccion3":
      "Dicta al cajero el número de referencia en esta ficha para que lo tecleé directamente.",
    "oxxoFicha.instruccion4": "Realiza el pago correspondiente con dinero en efectivo.",
    "oxxoFicha.comprobantePrefijo": "El cajero te entregará un comprobante impreso.",
    "oxxoFicha.conservaComprobante": "Conserva este comprobante como confirmación.",
    "oxxoFicha.footerPrefijo": "Al completar estos pasos recibirás un correo de",
    "oxxoFicha.footerSufijo": "confirmando tu pago.",
  },
  oxxoPayment: {
    "oxxoPayment.errorProcesandoPago": "Error procesando el pago: {{detalle}}",
    "oxxoPayment.instruccionesGenerar":
      "Presione el siguiente botón para generar la ficha de pago, esta contiene un número de referencia el cuál se deberá suministrar al cajero del oxxo al que asista.",
    "oxxoPayment.vigenciaPrefijo": "La ficha tiene una vigencia de ",
    "oxxoPayment.vigenciaSufijo":
      ", de no realizarse el pago en ese periodo el registro quedará eliminado y deberá hacer un nuevo registro.",
    "oxxoPayment.notificacionCorreo":
      "Al momento de realizar el pago recibirá una notificación vía correo electrónico con la confirmación de su inscripción y número asignado",
    "oxxoPayment.generarFicha": "Generar ficha de pago en oxxo",
  },
  tarjeta: {
    "tarjeta.codigoDescuento": "Código de descuento",
    "tarjeta.tituloTarjeta": "Tarjeta de crédito o débito",
    "tarjeta.ingresaDatos": "Ingresa los datos para continuar",
    "tarjeta.precioEspecialBanorte": "Este evento cuenta precios especiales para tarjetas BANORTE",
    "tarjeta.nombrePropietario": "Nombre del propietario",
    "tarjeta.campoRequerido": "Campo requerido",
    "tarjeta.numeroDeTarjeta": "Número de tarjeta",
    "tarjeta.descuentoBanorteAplicado":
      "✅ Precio especial tarjetahabientes Banorte descuento aplicado de {{descuento}} MXN",
    "tarjeta.montoTotalActual": "Monto total a pagar actual: {{monto}} MXN",
    "tarjeta.fechaDeVencimiento": "Fecha de vencimiento",
    "tarjeta.mes": "Mes",
    "tarjeta.anio": "Año",
    "tarjeta.codigoDeSeguridad": "Código de Seguridad",
    "tarjeta.realizarElPago": "Realizar el pago",
    "tarjeta.notaConfirmacion":
      "Nota: al completar tu pedido recibirás un correo de confirmación con tu compra",
    "tarjeta.revisaNumeroTarjeta": "Revisa el numero de tarjeta!",
    "tarjeta.revisaCodigoSeguridad": "Revisa el código de seguridad!",
    "tarjeta.revisaFechaExpiracion": "Revisa la fecha de expiración!",
    "tarjeta.errorPrefijo": "Error:",
  },
  codigoDescuento: {
    "codigoDescuento.codigoInvalido": "Código inválido, intente de nuevo: ",
    "codigoDescuento.siCuentasConCodigo": "Si cuentas con un código de descuento ingresalo aquí",
    "codigoDescuento.continuaProceso":
      "Una vez aplicado el código debes continuar el proceso presionando el botón que se muestra abajo",
    "codigoDescuento.aplicarCodigo": "Aplicar el código",
    "codigoDescuento.codigoAplicado": "Código aplicado",
  },
  openpay: {
    "openpay.errorCargando": "Error cargando Openpay. Intenta recargar la página.",
    "openpay.noListo": "Openpay aún no está listo. Recarga la página.",
    "openpay.sinDeviceSession": "No se pudo generar device_session_id. Recarga la página.",
    "openpay.revisaNumero": "Revisa el número de tarjeta.",
    "openpay.revisaMes": "Revisa el mes de expiración.",
    "openpay.revisaAnio": "Revisa el año de expiración.",
    "openpay.revisaCvc": "Revisa el CVC.",
    "openpay.pagoNoCompletado": "Pago no completado. Intenta nuevamente.",
    "openpay.errorInternoToken": "Error interno al procesar el token.",
    "openpay.errorTokenizando": "Error tokenizando la tarjeta.",
    "openpay.noSePudoTokenizar": "No se pudo tokenizar la tarjeta (Openpay).",
    "openpay.cargando": "Cargando Openpay...",
    "openpay.confirmandoPago": "Confirmando tu pago...",
    "openpay.confirmandoPagoIntento": "Confirmando tu pago... ({{intento}}/{{total}})",
    "openpay.pagoConfirmadoFinalizando": "Pago confirmado. Finalizando...",
    "openpay.pagoConfirmadoErrorActualizar":
      "Pago confirmado, pero no se pudo actualizar el registro. Contacta soporte con tu folio.",
    "openpay.pagoNoPudoCompletarse": "El pago no pudo completarse.",
    "openpay.pagoRechazadoCancelado": "El pago fue rechazado o cancelado.",
    "openpay.pendienteTimeout": "Aún no se confirma el pago. Puedes reintentar la validación.",
    "openpay.faltaInformacion": "Falta información para confirmar el pago.",
    "openpay.noCierresVentana":
      "No cierres esta ventana. Estamos validando la autorización 3D Secure.",
    "openpay.reintentarConfirmacion": "Reintentar confirmación",
    "openpay.intentarDeNuevo": "Intentar de nuevo",
  },
  confirmacion: {
    "confirmacion.confirmacionPendiente": "Confirmación pendiente",
    "confirmacion.pagoConfirmado": "Tu pago fue confirmado.",
    "confirmacion.asignandoFolio":
      "Estamos asignando tu folio/número de dorsal definitivo, esto puede tardar unos segundos...",
    "confirmacion.recibirasCorreoFolio":
      "Vas a recibir un correo de confirmación con tu folio oficial en cuanto sea asignado.",
    "confirmacion.confirmacionGrupo": "Confirmación de inscripción del grupo",
    "confirmacion.verConfirmacionCadaUno":
      "Para ver la confirmación de cada uno presiona los siguientes botones",
    "confirmacion.deslindeResponsabilidades": "Deslinde de responsabilidades",
    "confirmacion.dorsalDigital": "Dorsal digital",
    "confirmacion.cargandoConfirmacion": "Cargando confirmación...",
    "confirmacion.completarPago": "Completar pago",
    "confirmacion.registroCapturadoPagoIncompleto":
      "Tu registro fue capturado, pero el pago quedó incompleto. Puedes finalizarlo aquí para generar un nuevo registro confirmado.",
    "confirmacion.pagoRealizadoCorrectamente":
      "Pago realizado correctamente. El nuevo registro fue procesado.",
    "confirmacion.guardandoNuevoRegistro": "Guardando nuevo registro...",
    "confirmacion.registroDetectadoPagoIncompleto": "Registro detectado con pago incompleto",
    "confirmacion.revisaDatosCompletaPago":
      "Revisa tus datos y completa el pago para generar un nuevo registro",
    "confirmacion.errorGuardandoRegistro": "Ocurrió un error al guardar el nuevo registro.",
    "confirmacion.errorDurantePago": "Ocurrió un error durante el pago.",
    "confirmacion.vistaPreviaPdf": "Vista previa del PDF",
    "confirmacion.actualizarVistaPrevia": "Actualizar vista previa",
    "confirmacion.descargarPdf": "Descargar PDF",
    "confirmacion.generandoPdf": "Generando PDF...",
    "confirmacion.noSePudoGenerarVistaPrevia": "No se pudo generar la vista previa",
    "confirmacion.errorAlGenerarPdf": "Error al generar el PDF",
    "confirmacion.errorAlDescargarPdf": "Error al descargar el PDF",
  },
  pagareTarjeta: {
    "pagareTarjeta.declaracionPrefijo":
      "De igual manera declaro que realice el pago de la inscripción via web con la tarjeta",
    "pagareTarjeta.registradaConNombre": "registrada con el nombre de usuario",
    "pagareTarjeta.ultimosDigitos": "cuyos últimos 4 digitos son",
    "pagareTarjeta.porUnMonto": "por un monto de",
    "pagareTarjeta.monto": "$ {{monto}} MXN",
    "pagareTarjeta.aNombreDe": "a nombre de",
  },
  contador: {
    "contador.dias": "Días",
    "contador.horas": "Horas",
    "contador.minutos": "Minutos",
    "contador.segundos": "Segundos",
  },
};

const NOMBRES_SECCIONES = {
  general: "General",
  camposFormulario: "Campos del formulario de datos",
  modalidadesCategorias: "Modalidades y categorías",
  moneda: "Moneda",
  producto: "Productos adicionales",
  detalleOrden: "Detalle de la orden",
  grupo: "Registro en grupo",
  pago: "Métodos de pago",
  guardarYAgregar: "Botones de continuar/guardar",
  confirmarCorreo: "Confirmación de correo",
  oxxo: "Oxxo (ficha de inscripción)",
  oxxoFicha: "Oxxo (ficha de confirmación)",
  oxxoPayment: "Oxxo (generar pago)",
  tarjeta: "Pago con tarjeta",
  codigoDescuento: "Código de descuento",
  openpay: "Pago con tarjeta (Openpay)",
  confirmacion: "Pantallas de confirmación",
  pagareTarjeta: "Pagaré de tarjeta",
  contador: "Contador (días/horas/minutos/segundos)",
};

// La mayoría de las secciones existentes nunca recibieron un sectionID
// (es un campo opcional pensado para anclas de navegación, no para esto).
// Sin un identificador estable no hay dónde guardar/leer sus traducciones,
// así que la primera vez que se abre esta tabla se le asigna uno y se
// persiste de una sola vez — después de eso, se usa siempre ese mismo valor.
function generarSectionID(componentType) {
  return `${componentType || "seccion"}_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

function filaTraducibleField({ eventoIndex, dispatch, sectionID, campo, valorBase, idiomas, traduccionesSeccion }) {
  const entradaCampo = (traduccionesSeccion && traduccionesSeccion[campo.fieldName]) || {};
  // Red de seguridad: si por algún esquema no detectado a tiempo el valor
  // real resulta ser un objeto/arreglo en vez de texto/URL, se evita
  // reventar toda la pantalla del admin ("Objects are not valid as a React
  // child") — se trata como si no hubiera valor, en vez de intentar
  // renderizarlo.
  const valorBaseSeguro =
    valorBase && typeof valorBase === "object" ? "" : valorBase;

  function guardarTraduccion(idioma, value) {
    const nuevoValor = Object.values(value)[0];
    dispatch(
      updateEventAttribute(
        eventoIndex,
        `web/idiomas/traducciones/${sectionID}/${campo.fieldName}/`,
        { [idioma]: nuevoValor }
      )
    );
  }

  return (
    <TableRow key={campo.fieldName}>
      <TableCell>{campo.label || campo.fieldName}</TableCell>
      <TableCell>
        {campo.type === "photo" || campo.type === "file" ? (
          valorBaseSeguro ? (
            <img src={valorBaseSeguro} alt="" style={{ maxWidth: 100, maxHeight: 60 }} />
          ) : (
            "—"
          )
        ) : (
          valorBaseSeguro || "—"
        )}
      </TableCell>
      {idiomas.map((idioma) => (
        <TableCell key={idioma}>
          <EditableField
            fieldObject={{
              fieldName: campo.fieldName,
              label: IDIOMAS_LABELS[idioma] || idioma,
              type: campo.type,
              defaultValue: entradaCampo[idioma] || valorBaseSeguro || "",
              required: false,
              errorMessage: "",
              textSize: "body2",
            }}
            onSubmit={(value) => guardarTraduccion(idioma, value)}
          />
        </TableCell>
      ))}
    </TableRow>
  );
}

function filaTraducibleArrayItem({
  eventoIndex,
  dispatch,
  sectionID,
  campo,
  subCampo,
  indexArray,
  valorBaseItem,
  idiomas,
  traduccionesSeccion,
}) {
  const entradaCampo = (traduccionesSeccion && traduccionesSeccion[campo.fieldName]) || {};
  const entradaItem = entradaCampo[indexArray] || {};
  const entradaSubCampo = entradaItem[subCampo.fieldName] || {};

  const valorBaseSeguro =
    valorBaseItem && typeof valorBaseItem === "object" ? "" : valorBaseItem;

  function guardarTraduccion(idioma, value) {
    const nuevoValor = Object.values(value)[0];
    dispatch(
      updateEventAttribute(
        eventoIndex,
        `web/idiomas/traducciones/${sectionID}/${campo.fieldName}/${indexArray}/${subCampo.fieldName}/`,
        { [idioma]: nuevoValor }
      )
    );
  }

  return (
    <TableRow key={`${campo.fieldName}-${indexArray}-${subCampo.fieldName}`}>
      <TableCell>
        {(campo.label || campo.fieldName) +
          ` · Item ${parseInt(indexArray, 10) + 1} · ` +
          (subCampo.label || subCampo.fieldName)}
      </TableCell>
      <TableCell>
        {subCampo.type === "photo" || subCampo.type === "file" ? (
          valorBaseSeguro ? (
            <img src={valorBaseSeguro} alt="" style={{ maxWidth: 100, maxHeight: 60 }} />
          ) : (
            "—"
          )
        ) : (
          valorBaseSeguro || "—"
        )}
      </TableCell>
      {idiomas.map((idioma) => (
        <TableCell key={idioma}>
          <EditableField
            fieldObject={{
              fieldName: `${campo.fieldName}_${indexArray}_${subCampo.fieldName}`,
              label: IDIOMAS_LABELS[idioma] || idioma,
              type: subCampo.type,
              defaultValue: entradaSubCampo[idioma] || valorBaseSeguro || "",
              required: false,
              errorMessage: "",
              textSize: "body2",
            }}
            onSubmit={(value) => guardarTraduccion(idioma, value)}
          />
        </TableCell>
      ))}
    </TableRow>
  );
}

export default function TraduccionesSeccionTable(props) {
  const { idiomas } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const eventDetail = useSelector((state) => state.events.eventDetail);
  const eventWeb = useSelector((state) => state.events.eventWeb);

  const eventoIndex = eventDetail && "evento_" + eventDetail.iDEvento;
  const vistas = (eventWeb && eventWeb.views) || [];
  const vistasTraducibles = vistas
    .map((item, index) => ({ item, index }))
    .filter(
      ({ item }) =>
        obtenerCamposTraducibles(item.component).length > 0 ||
        obtenerCamposArrayTraducibles(item.component).length > 0
    );
  const traducciones = (eventWeb && eventWeb.idiomas && eventWeb.idiomas.traducciones) || {};

  const sectionIDsFaltantes = vistasTraducibles
    .filter(({ item }) => !item.componentConfig.sectionID)
    .map(({ index }) => index);

  useEffect(() => {
    if (!eventoIndex || sectionIDsFaltantes.length === 0) return;
    sectionIDsFaltantes.forEach((index) => {
      const vistaFaltante = vistas[index];
      dispatch(
        updateEventAttribute(eventoIndex, `web/views/${index}/componentConfig/`, {
          sectionID: generarSectionID(vistaFaltante && vistaFaltante.component),
        })
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoIndex, JSON.stringify(sectionIDsFaltantes)]);

  function guardarTextoFijo(idioma, clave, value) {
    const nuevoValor = Object.values(value)[0];
    dispatch(
      updateEventAttribute(eventoIndex, `web/idiomas/traduccionesFijas/${idioma}/`, {
        [clave]: nuevoValor,
      })
    );
  }

  const traduccionesFijasGuardadas =
    (eventWeb && eventWeb.idiomas && eventWeb.idiomas.traduccionesFijas) || {};

  if (!idiomas || idiomas.length === 0) {
    return null;
  }

  return (
    <div>
      <Typography className={classes.seccionTitulo} gutterBottom variant={"h5"} component="h3">
        Secciones de la web para ajustar el idioma
      </Typography>
      <Typography gutterBottom variant={"body2"} color="textSecondary">
        Deja la casilla vacía para usar el mismo texto/imagen en ese idioma que
        en español.
      </Typography>

      {vistasTraducibles.length === 0 && (
        <Typography gutterBottom variant={"body2"} color="textSecondary">
          Esta web todavía no tiene secciones con contenido traducible agregadas.
        </Typography>
      )}

      {vistasTraducibles.map(({ item: vista, index }) => {
        const sectionID = vista.componentConfig.sectionID;
        const nombreSeccion =
          vista.componentConfig.title ||
          (Components[vista.component] &&
            Components[vista.component].default &&
            Components[vista.component].default.displayName) ||
          vista.component;
        if (!sectionID) {
          return (
            <Typography key={index} gutterBottom variant={"body2"} color="textSecondary">
              Preparando "{nombreSeccion}"...
            </Typography>
          );
        }
        const campos = obtenerCamposTraducibles(vista.component);
        const camposArray = obtenerCamposArrayTraducibles(vista.component);
        const traduccionesSeccion = traducciones[sectionID];

        const filasArray = [];
        camposArray.forEach((campo) => {
          const valorArray = vista.componentConfig[campo.fieldName];
          if (!valorArray || typeof valorArray !== "object") return;
          Object.keys(valorArray).forEach((indexArray) => {
            campo.subCamposTraducibles.forEach((subCampo) => {
              filasArray.push(
                filaTraducibleArrayItem({
                  eventoIndex,
                  dispatch,
                  sectionID,
                  campo,
                  subCampo,
                  indexArray,
                  valorBaseItem:
                    valorArray[indexArray] &&
                    valorArray[indexArray][subCampo.fieldName],
                  idiomas,
                  traduccionesSeccion,
                })
              );
            });
          });
        });

        return (
          <Accordion key={sectionID}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>
                {nombreSeccion}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Campo</TableCell>
                    <TableCell>Español (base)</TableCell>
                    {idiomas.map((idioma) => (
                      <TableCell key={idioma}>{IDIOMAS_LABELS[idioma] || idioma}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campos.map((campo) =>
                    filaTraducibleField({
                      eventoIndex,
                      dispatch,
                      sectionID,
                      campo,
                      valorBase: vista.componentConfig[campo.fieldName],
                      idiomas,
                      traduccionesSeccion,
                    })
                  )}
                  {filasArray}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Divider style={{ margin: "30px 0" }} />

      <Typography className={classes.seccionTitulo} gutterBottom variant={"h5"} component="h3">
        Textos fijos de la inscripción
      </Typography>
      <Typography gutterBottom variant={"body2"} color="textSecondary">
        Estos son los textos del flujo de inscripción que ya se traducen
        automáticamente. Aquí puedes personalizarlos para este evento si lo
        necesitas.
      </Typography>

      {Object.keys(TEXTOS_FIJOS_BASE).map((seccionKey) => (
        <Accordion key={seccionKey}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{NOMBRES_SECCIONES[seccionKey] || seccionKey}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Clave</TableCell>
                  <TableCell>Español (base)</TableCell>
                  {idiomas.map((idioma) => (
                    <TableCell key={idioma}>{IDIOMAS_LABELS[idioma] || idioma}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(TEXTOS_FIJOS_BASE[seccionKey]).map((clave) => (
                  <TableRow key={clave}>
                    <TableCell className={classes.celdaClave}>{clave}</TableCell>
                    <TableCell className={classes.celdaBase}>
                      {TEXTOS_FIJOS_BASE[seccionKey][clave]}
                    </TableCell>
                    {idiomas.map((idioma) => (
                      <TableCell key={idioma}>
                        <EditableField
                          fieldObject={{
                            fieldName: clave,
                            label: IDIOMAS_LABELS[idioma] || idioma,
                            type: "text",
                            defaultValue:
                              (traduccionesFijasGuardadas[idioma] &&
                                traduccionesFijasGuardadas[idioma][clave]) ||
                              TEXTOS_FIJOS_BASE[seccionKey][clave] ||
                              "",
                            required: false,
                            errorMessage: "",
                            textSize: "body2",
                          }}
                          onSubmit={(value) => guardarTextoFijo(idioma, clave, value)}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
}
